import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  Users, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Home, 
  SkipBack, 
  SkipForward, 
  CalendarIcon,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, getWeek, isWeekend, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { exportTeamAvailability, exportTeamAvailabilityPDF } from '../shared/ExcelExport';
import { API, AVAILABILITY_TYPES, getAvailabilityTypeInfo, formatDateDDMMYYYY, generateWeekOptions } from './utils/helpers';

const TeamAvailabilityTab = ({ employees }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ 
    startDate: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    endDate: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const fetchedRef = useRef('');

  // Memoize week calculations
  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);
  const weekNumber = useMemo(() => getWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);

  // Week options for dropdown
  const weekOptions = useMemo(() => generateWeekOptions(currentDate), [currentDate]);
  const currentWeekValue = useMemo(() => format(weekStart, 'yyyy-MM-dd'), [weekStart]);

  // Create a stable key for fetching
  const fetchKey = useMemo(() => format(weekStart, 'yyyy-MM-dd'), [weekStart]);

  // Fetch company info on mount
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${API}/company/profile`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    if (fetchedRef.current === fetchKey) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const startStr = format(weekStart, 'yyyy-MM-dd');
        const endStr = format(weekEnd, 'yyyy-MM-dd');
        
        const [availRes, holidaysRes] = await Promise.all([
          axios.get(`${API}/availability/calendar?start_date=${startStr}&end_date=${endStr}`),
          axios.get(`${API}/holidays/poland/${currentDate.getFullYear()}`)
        ]);
        
        setAvailability(availRes.data);
        setHolidays(holidaysRes.data);
        fetchedRef.current = fetchKey;
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchKey, weekStart, weekEnd, currentDate]);

  const navigateWeek = (direction) => {
    setCurrentDate(prev => addDays(prev, direction * 7));
  };

  const goToCurrentWeek = () => {
    setCurrentDate(new Date());
  };

  const handleWeekDropdownChange = (value) => {
    setCurrentDate(new Date(value));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setCurrentDate(date);
      setDatePickerOpen(false);
    }
  };

  const goToMonthStart = () => {
    setCurrentDate(startOfMonth(currentDate));
  };

  const goToMonthEnd = () => {
    setCurrentDate(endOfMonth(currentDate));
  };

  const isHoliday = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.find(h => h.date === dateStr);
  }, [holidays]);

  const getEmployeeAvailability = useCallback((employeeId, date) => {
    return availability.filter(a => {
      const start = new Date(a.start_date);
      const end = new Date(a.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      const checkDate = new Date(date);
      checkDate.setHours(12, 0, 0, 0);
      return a.employee_id === employeeId && checkDate >= start && checkDate <= end;
    });
  }, [availability]);

  // Export Team Overview to Excel
  const handleExport = async () => {
    setExportLoading(true);
    try {
      // Fetch availability data for the export range
      const startYear = new Date(exportRange.startDate).getFullYear();
      const endYear = new Date(exportRange.endDate).getFullYear();
      
      const [availRes, ...holidayResponses] = await Promise.all([
        axios.get(`${API}/availability/calendar?start_date=${exportRange.startDate}&end_date=${exportRange.endDate}`),
        ...Array.from(new Set([startYear, endYear])).map(year => 
          axios.get(`${API}/holidays/poland/${year}`)
        )
      ]);
      
      const allHolidays = holidayResponses.flatMap(r => r.data);
      
      await exportTeamAvailability({
        employees,
        availability: availRes.data,
        holidays: allHolidays,
        startDate: exportRange.startDate,
        endDate: exportRange.endDate,
        getAvailabilityTypeInfo,
        companyInfo
      });
      
      toast.success(t('auto.ctr.excelFileDownloaded'));
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(t('auto.ctr.failedToExport'));
    } finally {
      setExportLoading(false);
    }
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (!exportRange.startDate || !exportRange.endDate) {
      toast.error(t('auto.ctr.pleaseSelectADateRange'));
      return;
    }

    setExportLoading(true);
    try {
      const startYear = new Date(exportRange.startDate).getFullYear();
      const endYear = new Date(exportRange.endDate).getFullYear();
      
      const [availRes, ...holidayResponses] = await Promise.all([
        axios.get(`${API}/availability/calendar?start_date=${exportRange.startDate}&end_date=${exportRange.endDate}`),
        ...Array.from(new Set([startYear, endYear])).map(year => 
          axios.get(`${API}/holidays/poland/${year}`)
        )
      ]);
      
      const allHolidays = holidayResponses.flatMap(r => r.data);
      
      await exportTeamAvailabilityPDF({
        employees,
        availability: availRes.data,
        holidays: allHolidays,
        startDate: exportRange.startDate,
        endDate: exportRange.endDate,
        getAvailabilityTypeInfo,
        companyInfo
      });
      
      toast.success(t('auto.ctr.pdfFileDownloaded'));
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('auto.ctr.failedToExportPdf'));
    } finally {
      setExportLoading(false);
    }
  };

  // Open export dialog with current week as default
  const openExportDialog = () => {
    setExportRange({
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd')
    });
    setExportDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('auto.ctr.teamAvailabilityOverview')}
              </CardTitle>
              <CardDescription>
                Week {weekNumber} • {formatDateDDMMYYYY(weekStart)} - {formatDateDDMMYYYY(weekEnd)}
              </CardDescription>
            </div>
            {/* Navigation Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={goToMonthStart} title={t('auto.ctr.monthStart')} data-testid="btn-gotomonthstart">
                <SkipBack className="h-4 w-4" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} title={t('auto.ctr.previousWeek')}>
                <ChevronLeft className="h-4 w-4 mr-1" /> {t('auto.ctr.prev')}
              </Button>
              
              {/* Week Dropdown */}
              <Select value={currentWeekValue} onValueChange={handleWeekDropdownChange}>
                <SelectTrigger className="w-[280px]">
                  <SelectValue placeholder={t('auto.ctr.selectAWeek')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {weekOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date Picker */}
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {t('auto.ctr.pickDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={currentDate}
                    onSelect={handleDateSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} title={t('auto.ctr.nextWeek')} data-testid="btn-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={goToMonthEnd} title={t('auto.ctr.monthEnd')} data-testid="btn-gotomonthend">
                <SkipForward className="h-4 w-4" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={goToCurrentWeek} title={t('auto.ctr.currentWeek')} data-testid="btn-gotocurrentweek">
                <Home className="h-4 w-4" />
              </Button>

              {/* Export to Excel */}
              <div className="border-l pl-2 ml-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={openExportDialog}
                  className="bg-green-50 hover:bg-green-100 border-green-200 text-green-700"
                  title={t('auto.ctr.exportToExcel')}
                  data-testid="export-team-overview-btn"
                >
                  <Download className="h-4 w-4 mr-1" /> {t('auto.ctr.export')}
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[150px] sticky left-0 bg-white z-10">{t('auto.ctr.employee')}</TableHead>
                    {weekDays.map((day) => {
                      const holiday = isHoliday(day);
                      const weekend = isWeekend(day);
                      return (
                        <TableHead 
                          key={day.toISOString()} 
                          className={`text-center min-w-[100px] ${weekend ? 'bg-gray-100' : ''} ${holiday ? 'bg-red-50' : ''}`}
                        >
                          <div className="text-xs text-gray-500">{format(day, 'EEE')}</div>
                          <div className={holiday ? 'text-red-600' : ''}>{formatDateDDMMYYYY(day)}</div>
                          {holiday && (
                            <div className="text-xs text-red-600 truncate" title={holiday.name}>
                              {holiday.name}
                            </div>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.filter(e => e.status === 'active' || e.is_active !== false).map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell className="font-medium sticky left-0 bg-white z-10">
                        {employee.name}
                      </TableCell>
                      {weekDays.map((day) => {
                        const empAvail = getEmployeeAvailability(employee.id, day);
                        const holiday = isHoliday(day);
                        const weekend = isWeekend(day);
                        
                        return (
                          <TableCell 
                            key={day.toISOString()} 
                            className={`text-center ${weekend ? 'bg-gray-100' : ''} ${holiday ? 'bg-red-50' : ''}`}
                          >
                            {empAvail.length > 0 ? (
                              <div className="space-y-1">
                                {empAvail.map((a) => (
                                  <Badge 
                                    key={a.id} 
                                    className={getAvailabilityTypeInfo(a.availability_type).color + ' text-xs'}
                                    title={a.reason || getAvailabilityTypeInfo(a.availability_type).label}
                                  >
                                    {getAvailabilityTypeInfo(a.availability_type).shortLabel}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-green-600 text-xs">{t('auto.ctr.available')}</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="font-medium">{t('auto.ctr.legend')}</span>
            {AVAILABILITY_TYPES.map((type) => (
              <span key={type.value} className="flex items-center gap-1">
                <Badge className={type.color + ' text-xs'}>{type.shortLabel}</Badge>
                <span className="text-gray-600">{type.label}</span>
              </span>
            ))}
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-red-50 border border-red-200 rounded"></span>
              <span className="text-gray-600">{t('auto.ctr.holiday')}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></span>
              <span className="text-gray-600">{t('auto.ctr.weekend')}</span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.ctr.exportTeamAvailability')}</DialogTitle>
            <DialogDescription>
              {t('auto.ctr.selectADateRangeForThe')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('auto.ctr.startDate2')}</Label>
                <Input
                  type="date"
                  value={exportRange.startDate}
                  onChange={(e) => setExportRange(prev => ({ ...prev, startDate: e.target.value  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('auto.ctr.endDate2')}</Label>
                <Input
                  type="date"
                  value={exportRange.endDate}
                  onChange={(e) => setExportRange(prev => ({ ...prev, endDate: e.target.value  }))}
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                setExportRange({
                  startDate: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
                  endDate: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')
                });
              }} data-testid="btn-current-week">{t('auto.ctr.currentWeek2')}</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                setExportRange({
                  startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
                  endDate: format(endOfMonth(now), 'yyyy-MM-dd')
                });
              }} data-testid="btn-current-month">{t('auto.ctr.currentMonth')}</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                const start = startOfMonth(now);
                setExportRange({
                  startDate: format(start, 'yyyy-MM-dd'),
                  endDate: format(endOfMonth(addMonths(start, 2)), 'yyyy-MM-dd')
                });
              }} data-testid="btn-next-3-months">{t('auto.ctr.next3Months')}</Button>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
            <Button onClick={handleExportPDF} disabled={exportLoading} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="dialog-exportpdf-btn">
              {exportLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              Export to PDF
            </Button>
            <Button onClick={handleExport} disabled={exportLoading} className="bg-green-600 hover:bg-green-700" data-testid="dialog-export-btn">
              {exportLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamAvailabilityTab;
